import { useQuery } from '@tanstack/react-query';
import { Download01, Eye } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { getApiBaseUrl } from '@/lib/config';
import { fileService } from '@/lib/files/service';
import type { CrmObject } from '@/types/crm';

interface FileRecordOverviewProps {
  object: CrmObject;
}

/**
 * Custom overview for ahoy_file entities in the slideout.
 *
 * Shows a prominent "View file" / "Download" button so the user can
 * access the underlying file directly from the file entity's slideout.
 */
export function FileRecordOverview({ object }: FileRecordOverviewProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? object.org_id;

  const { data: uploadedFile, isLoading } = useQuery({
    queryKey: ['files', 'byEntity', orgId, object.entity_id],
    queryFn: () => fileService.getByEntityId(orgId, object.entity_id),
    enabled: Boolean(orgId) && Boolean(object.entity_id),
    staleTime: 5 * 60 * 1000,
  });

  const viewUrl = useMemo(() => {
    if (!uploadedFile) {
      return null;
    }
    // Use file_url from backend if available (presigned S3 URL).
    // Fall back to constructing the local-storage proxy URL.
    return (
      uploadedFile.file_url ??
      `${getApiBaseUrl()}/api/orgs/${orgId}/files/${uploadedFile.id}/view/`
    );
  }, [uploadedFile, orgId]);

  const downloadUrl = useMemo(() => {
    if (!uploadedFile) {
      return null;
    }
    return (
      uploadedFile.download_url ??
      `${getApiBaseUrl()}/api/orgs/${orgId}/files/${uploadedFile.id}/download/`
    );
  }, [uploadedFile, orgId]);

  const handleView = useCallback(() => {
    if (viewUrl) {
      window.open(viewUrl, '_blank', 'noopener,noreferrer');
    }
  }, [viewUrl]);

  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    }
  }, [downloadUrl]);

  if (isLoading) {
    return (
      <div className='flex gap-2'>
        <div className='h-9 w-24 bg-[var(--color-bg-tertiary)] rounded-lg animate-pulse' />
        <div className='h-9 w-24 bg-[var(--color-bg-tertiary)] rounded-lg animate-pulse' />
      </div>
    );
  }

  if (!uploadedFile) {
    return null;
  }

  return (
    <div className='flex items-center gap-2'>
      <Button size='sm' color='primary' onClick={handleView} iconLeading={Eye}>
        View file
      </Button>
      <Button
        size='sm'
        color='secondary'
        onClick={handleDownload}
        iconLeading={Download01}
      >
        Download
      </Button>
    </div>
  );
}
